/* eslint-disable react-refresh/only-export-components */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { CircleX } from 'lucide-react'

export type ToastType = 'success' | 'warning' | 'error' | 'info'
export type ToastTone = ToastType | 'default' | 'danger'

export type ToastOptions = {
  type: ToastType
  title: string
  message: string
  duration?: number
}

type ToastItem = ToastOptions & {
  id: string
}

type ToastContextValue = {
  showToast: (toast: ToastOptions) => string
  closeToast: (id: string) => void
}

const toastContext = createContext<ToastContextValue | null>(null)

const defaultDuration = 3000

const toastStyles: Record<ToastType, { border: string; title: string }> = {
  success: {
    border: 'border-l-success',
    title: 'text-success-text',
  },
  warning: {
    border: 'border-l-warning',
    title: 'text-warning-text',
  },
  error: {
    border: 'border-l-error',
    title: 'text-error-text',
  },
  info: {
    border: 'border-l-info',
    title: 'text-info-text',
  },
}

const toastTitles: Record<ToastType, string> = {
  success: 'Success',
  warning: 'Warning',
  error: 'Error',
  info: 'Information',
}

let externalShowToast: ((toast: ToastOptions) => string) | null = null

function createToastId() {
  return `toast-${Date.now()}-${Math.random().toString(36).slice(2)}`
}

export function getToastTitle(type: ToastType) {
  return toastTitles[type]
}

export function showToast(toast: ToastOptions) {
  if (!externalShowToast) {
    return ''
  }

  return externalShowToast(toast)
}

export function resolveToastType(tone: ToastTone = 'info'): ToastType {
  if (tone === 'danger') {
    return 'error'
  }

  if (tone === 'default') {
    return 'info'
  }

  return tone
}

export function showToastMessage(message: unknown, tone: ToastTone = 'info', duration?: number) {
  const type = resolveToastType(tone)

  let formattedMessage = 'An unexpected error occurred.'
  if (typeof message === 'string') {
    formattedMessage = message
  } else if (Array.isArray(message)) {
    formattedMessage = message.map((item) => (typeof item === 'string' ? item : JSON.stringify(item))).join(', ')
  } else if (message && typeof message === 'object') {
    const msgObj = message as any
    const responseData = msgObj?.response?.data
    const raw =
      responseData?.error?.message ??
      responseData?.message ??
      responseData?.errors ??
      responseData?.detail ??
      responseData?.error ??
      msgObj?.error?.message ??
      msgObj?.message ??
      msgObj?.error

    if (Array.isArray(raw)) {
      formattedMessage = raw
        .map((item) => (typeof item === 'string' ? item.trim() : typeof item === 'object' && item?.message ? String(item.message).trim() : String(item ?? '').trim()))
        .filter(Boolean)
        .join(', ')
    } else if (typeof raw === 'string' && raw.trim()) {
      formattedMessage = raw.trim()
    } else if (raw && typeof raw === 'object') {
      const nestedMsg = (raw as any)?.message || (raw as any)?.error
      if (Array.isArray(nestedMsg)) {
        formattedMessage = nestedMsg.filter(Boolean).join(', ')
      } else if (typeof nestedMsg === 'string' && nestedMsg.trim()) {
        formattedMessage = nestedMsg.trim()
      } else {
        formattedMessage = JSON.stringify(raw)
      }
    } else {
      formattedMessage = String(message)
    }
  }

  return showToast({
    type,
    title: getToastTitle(type),
    message: formattedMessage,
    duration,
  })
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([])
  const timersRef = useRef<Map<string, number>>(new Map())

  const closeToast = useCallback((id: string) => {
    const timer = timersRef.current.get(id)

    if (timer) {
      window.clearTimeout(timer)
      timersRef.current.delete(id)
    }

    setToasts((current) => current.filter((toast) => toast.id !== id))
  }, [])

  const showToastValue = useCallback((toast: ToastOptions) => {
    let existingId: string | null = null
    setToasts((current) => {
      const match = current.find((t) => t.message === toast.message && t.type === toast.type)
      if (match) {
        existingId = match.id
      }
      return current
    })

    if (existingId) {
      return existingId
    }

    const id = createToastId()

    setToasts((current) => [...current, { ...toast, id }])

    if (toast.duration !== 0) {
      const timer = window.setTimeout(() => {
        closeToast(id)
      }, toast.duration ?? defaultDuration)

      timersRef.current.set(id, timer)
    }

    return id
  }, [closeToast])

  useEffect(() => {
    externalShowToast = showToastValue

    return () => {
      if (externalShowToast === showToastValue) {
        externalShowToast = null
      }
    }
  }, [showToastValue])

  useEffect(() => {
    const timers = timersRef.current

    return () => {
      timers.forEach((timer) => window.clearTimeout(timer))
      timers.clear()
    }
  }, [])

  const value = useMemo(() => ({ showToast: showToastValue, closeToast }), [closeToast, showToastValue])

  return (
    <toastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} onClose={closeToast} />
    </toastContext.Provider>
  )
}

export function useToast() {
  const context = useContext(toastContext)

  if (!context) {
    throw new Error('useToast must be used within ToastProvider')
  }

  return context
}

function ToastViewport({ toasts, onClose }: { toasts: ToastItem[]; onClose: (id: string) => void }) {
  if (!toasts.length) {
    return null
  }

  return (
    <div
      className="pointer-events-none fixed right-5 top-5 z-[100000] flex w-[330px] max-w-[90vw] flex-col gap-[10px]"
      aria-live="polite"
      aria-atomic="false"
    >
      {toasts.map((toast) => {
        const styles = toastStyles[toast.type]

        return (
          <div
            key={toast.id}
            className={`pointer-events-auto flex min-h-[72px] h-auto w-full items-start justify-between border-l-[4px] border-y border-r border-y-[#eeeeee] border-r-[#eeeeee] bg-white px-4 py-3.5 shadow-md ${styles.border} rounded-l-[12px] transition-all animate-fade-in`}
            role={toast.type === 'error' ? 'alert' : 'status'}
          >
            <div className="min-w-0 pr-3 flex-1">
              <p className={`m-0 text-[15px] font-semibold leading-[22px] ${styles.title}`}>
                {toast.title}
              </p>
              <p className="m-0 mt-1 text-[13px] font-normal leading-[18px] text-neutral-600 break-words">
                {toast.message}
              </p>
            </div>
            <button
              type="button"
              aria-label="Close notification"
              className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-neutral-400 hover:text-neutral-700 focus:outline-none focus:ring-2 focus:ring-neutral-500/30 transition-colors"
              onClick={() => onClose(toast.id)}
              data-no-cta-proximity="true"
            >
              <CircleX className="h-5 w-5" strokeWidth={1.75} aria-hidden="true" />
            </button>
          </div>
        )
      })}
    </div>
  )
}
